import { useEffect, useState } from "react";
import client, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { Trash2, RotateCcw, Clock } from "lucide-react";
import { CloudIcon, LangIcon } from "@/components/brand-icons";
import { ConfirmDeleteDialog } from "@/components/confirm-delete-dialog";

function TrashedProject({ p, onRestore, onPurge }) {
  return (
    <Card className="border border-slate-200 bg-white p-5 shadow-none" data-testid={`trash-project-${p.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="truncate font-display text-base font-semibold text-slate-900" title={p.name}>{p.name}</h3>
          <p className="mt-1 flex items-center gap-2 font-mono-tight text-[11px] text-slate-500">
            <CloudIcon cloud={p.cloud} className="h-3.5 w-3.5" /> {p.cloud} · <LangIcon language={p.language} className="h-3.5 w-3.5" /> {p.language} · {p.region}
          </p>
        </div>
        <span className="inline-flex shrink-0 items-center gap-1 bg-amber-50 px-2 py-0.5 font-mono-tight text-[10px] uppercase tracking-widest text-amber-800" data-testid={`trash-days-left-${p.id}`}>
          <Clock className="h-3 w-3" /> {p.days_left} day{p.days_left === 1 ? "" : "s"} left
        </span>
      </div>
      <p className="mt-3 font-mono-tight text-[11px] text-slate-500">
        deleted {new Date(p.deleted_at).toLocaleDateString()} · auto-purge {new Date(p.purge_at).toLocaleDateString()}
      </p>
      <div className="mt-4 flex items-center justify-end gap-1 border-t border-slate-100 pt-3">
        <Button variant="ghost" size="sm" onClick={() => onRestore(p)} className="h-8 text-slate-700" data-testid={`trash-restore-${p.id}`}>
          <RotateCcw className="mr-1.5 h-3.5 w-3.5" /> Restore
        </Button>
        <Button variant="ghost" size="sm" onClick={() => onPurge(p)} className="h-8 text-rose-700 hover:bg-rose-50 hover:text-rose-800" data-testid={`trash-purge-${p.id}`}>
          <Trash2 className="mr-1.5 h-3.5 w-3.5" /> Delete permanently
        </Button>
      </div>
    </Card>
  );
}

export function ProjectsTrash({ onRestored }) {
  const [items, setItems] = useState(null);
  const [purging, setPurging] = useState(null);

  useEffect(() => {
    client.get("/projects", { params: { trashed: true } }).then((r) => setItems(r.data));
  }, []);

  const restore = async (p) => {
    try {
      const { data } = await client.post(`/projects/${p.id}/restore`);
      setItems((l) => l.filter((x) => x.id !== p.id));
      onRestored?.(data);
      toast.success(`"${data.name}" restored`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const purge = async (p) => {
    await client.delete(`/projects/${p.id}`, { params: { permanent: true } });
    setItems((l) => l.filter((x) => x.id !== p.id));
    toast.success("Project permanently deleted");
  };

  if (items === null) return <p className="text-sm text-slate-500">Loading trash…</p>;
  return (
    <div data-testid="projects-trash">
      <p className="mb-4 font-mono-tight text-[11px] uppercase tracking-widest text-slate-500">
        // trash · {items.length} project{items.length === 1 ? "" : "s"} · kept 30 days, then deleted automatically
      </p>
      {items.length === 0 ? (
        <div className="border border-dashed border-slate-300 bg-white p-12 text-center" data-testid="trash-empty">
          <Trash2 className="mx-auto h-8 w-8 text-slate-300" />
          <h2 className="mt-4 font-display text-lg font-semibold">Trash is empty</h2>
          <p className="mt-1 text-sm text-slate-500">Deleted projects stay here for 30 days so you can restore them.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
          {items.map((p) => <TrashedProject key={p.id} p={p} onRestore={restore} onPurge={setPurging} />)}
        </div>
      )}
      <ConfirmDeleteDialog open={!!purging} onOpenChange={(o) => !o && setPurging(null)} name={purging?.name || ""} title="Delete permanently" verb="Delete forever"
        description="This removes the project and all its generated files immediately. This cannot be undone." onConfirm={() => purge(purging)} testid="confirm-purge" />
    </div>
  );
}
